/**
 * Requirements Transformer Service
 * 
 * Pure transformation logic for converting between form data and API format.
 * This is business logic (data transformation rules), so it belongs in mapping-unit.
 * 
 * All methods are pure functions - same input always produces same output.
 */

import type { RequirementFormData, RequirementSubmission } from '../types/api';

export class RequirementsTransformer {
  /**
   * Transform form data to API submission format
   */
  static toApiFormat(formData: RequirementFormData): RequirementSubmission {
    return {
      odooVersion: formData.odooVersion || '18.0',
      businessDescription: formData.businessDescription,
      expectedUsers: parseInt(formData.expectedUsers, 10),
      industry: formData.industry,
      functionalRequirements: formData.specificNeeds || [],
      specificNeeds: formData.specificNeeds || [],
      securityLevel: formData.securityLevel || 'standard',
    };
  }

  /**
   * Transform API response back to form format
   */
  static toFormFormat(submission: RequirementSubmission): RequirementFormData {
    return {
      odooVersion: submission.odooVersion,
      businessDescription: submission.businessDescription,
      expectedUsers: submission.expectedUsers.toString(),
      industry: submission.industry,
      specificNeeds: submission.specificNeeds,
      securityLevel: submission.securityLevel,
    };
  }

  /**
   * Generate cache key for requirements (for deduplication)
   * Business rule: Same requirements = same cache key
   */
  static generateCacheKey(data: RequirementFormData): string {
    const odooVersion = data.odooVersion || '18.0';
    const industry = data.industry || 'general';
    const users = data.expectedUsers || '0';
    const needs = (data.specificNeeds || []).sort().join(',');
    
    return `${odooVersion}-${industry}-${users}-${needs}`;
  }

  /**
   * Normalize form data (trim strings, handle defaults)
   */
  static normalize(data: RequirementFormData): RequirementFormData {
    return {
      ...data,
      businessDescription: data.businessDescription?.trim() || '',
      industry: data.industry?.trim() || '',
      expectedUsers: data.expectedUsers?.trim() || '0',
      specificNeeds: (data.specificNeeds || []).map(need => need.trim()).filter(Boolean),
    };
  }

  /**
   * Check if two requirement forms are equivalent (business logic)
   */
  static areEquivalent(a: RequirementFormData, b: RequirementFormData): boolean {
    return this.generateCacheKey(this.normalize(a)) === this.generateCacheKey(this.normalize(b));
  }
}
